import { PlusCircleOutlined } from "@ant-design/icons";
import { Button, Form, message, Row, Space, Table, TableColumnsType, TableProps } from "antd";
import { useEffect, useState } from "react";
import { BsPencilSquare, BsTrashFill } from "react-icons/bs";
import VocsModal from "src/components/Modal";
import { CommonError, CommonSuccess } from "src/entity/common/menus-enum.enum";
import { initTankType, SettingTankType, Filter } from "src/entity/settings/tank";
import { createTankType, deleteTankType, updateTankType } from "src/services/settings.service";
import { tankStore } from "src/stores/tank-store";
import { componentPalette } from "src/theme/component-palette";
import { useShallow } from "zustand/react/shallow";
import TankTypeModal from "./TankTypeModal";
import axios from "axios";
import { canEditTable } from "../PermissionCheck/PermissionCheck";
import useNavigationMenu from "src/hook/useNavigationMenu";

type OnChange = NonNullable<TableProps<any>["onChange"]>;
type Filters = Parameters<OnChange>[1];

const TankTypeTable = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [mode, setMode] = useState<"insert" | "update">("insert");
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState<Filters>({});
  const [loading, setLoading] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);
  const [tankId, setTankId] = useState<string>("");
  const { permissions } = useNavigationMenu();

  const [form] = Form.useForm();

  const pageSize = 10;
  const [current, setCurrent] = useState(1);

  const tankType = tankStore(useShallow((state) => state.tankType));
  const totalTankType = tankStore(useShallow((state) => state.totalTankType));
  const getAllTankType = tankStore(useShallow((state) => state.getTankType));

  const filterTankType = tankStore(useShallow((state) => state.filterTankType));
  const getFilterTankType = tankStore(useShallow((state) => state.getFilterTankType));

  const onOpenModal = (mode: "insert" | "update", data: SettingTankType) => {
    // console.log("onOpenModal", data);
    setMode(mode);
    setShowModal(true);
    setTankId(data._id);
    form.setFieldsValue(data);
  };

  const onCloseModal = () => {
    setShowModal(false);
    setIsDuplicate(false);
    const formValue = form.getFieldsValue();
    if (formValue) {
      form.setFields(
        Object.keys(formValue).map((field) => ({
          name: field,
          errors: [],
        })),
      );
    }
  };

  const onSave = async (mode: "insert" | "update", data: SettingTankType) => {
    try {
      let response;
      setLoading(true);
      if (mode == "update") {
        if (tankId) {
          data = {
            ...data,
            _id: tankId,
          };
          response = await updateTankType(data);
        }
      } else {
        response = await createTankType(data);
      }
      if (response?.data.code === 200) {
        setLoading(false);
        setShowModal(false);
        fetchTankType();
        getFilterTankType();
        messageApi.open({
          type: "success",
          content: CommonSuccess.SAVED_SUCCESSFULL,
        });
      } else {
        setIsDuplicate(false);
        messageApi.error({
          type: "error",
          content: CommonError.SOMETHING_WRONG,
        });
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === 409) {
          setIsDuplicate(true);
        } else {
          setIsDuplicate(false);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const onTableChange: TableProps<SettingTankType>["onChange"] = (pagination: any, filters: any) => {
    let current = pagination.current;
    setFilter(filters);
    setCurrent(current);
  };

  const mapFilter = (key: string): Filter[] | [] => {
    const resultFilter = filterTankType.find((item) => item.key === key)?.filter;
    if (resultFilter) {
      const filter: Filter[] = resultFilter?.map((value: any) => {
        let text = value;
        return {
          text,
          value: value,
        };
      });
      return filter;
    } else {
      return [];
    }
  };

  const showDeleteConfirm = async (value: SettingTankType) => {
    const { _id } = value;
    VocsModal.delete({
      onOk: async () => {
        if (_id) {
          handleDelete(_id);
        }
      },
    });
  };

  const calPagination = (current?: number) => {
    if (current) {
      return (current - 1) * 10;
    }
  };

  const createParamFilter = () => {
    let queryParam = "";
    let offset = 0;
    let limit = pageSize;

    if (current) {
      let resultCalOffset = calPagination(current);
      if (resultCalOffset) {
        offset = resultCalOffset;
      }
      queryParam += `&offset=${offset}`;
    }
    if (limit) {
      queryParam += `&limit=${limit}`;
    }

    if (filter) {
      const str = Object.entries(filter)
        .filter(([, value]) => value)
        .map(([key, value]) => `&${key}=[${value?.join(",")}]`)
        .join("");
      queryParam += str;
    }

    return queryParam;
  };

  const fetchTankType = async () => {
    const query = createParamFilter();
    setLoading(true);
    await getAllTankType(query);
    setLoading(false);
  };

  const handleDelete = async (_id: string) => {
    try {
      setLoading(true);
      const response = await deleteTankType(_id);
      if (response.data.code === 200) {
        setLoading(false);
        messageApi.open({
          type: "success",
          content: CommonSuccess.DELETE_SUCCESSFULL,
        });
        fetchTankType();
      } else {
        setLoading(false);
        messageApi.open({
          type: "error",
          content: CommonError.SOMETHING_WRONG,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchTankType();
      await getFilterTankType();
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    fetchTankType();
  }, [current, filter]);

  const columns: TableProps<SettingTankType>["columns"] = [
    {
      title: "No.",
      align: "center",
      render: (_, __, index) => <div>{index + 1 + (current - 1) * pageSize}</div>,
    },
    {
      title: "Tank Type",
      dataIndex: "tankType",
      width: 140,
      filters: mapFilter("tankType"),
    },
    {
      title: "Description",
      dataIndex: "description",
      filters: mapFilter("description"),
    },
    {
      title: "Remark",
      dataIndex: "remark",
    },
    {
      title: "Action",
      key: "action",
      render: (value, record) => (
        <Space>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => {
              onOpenModal("update", record);
            }}
            disabled={!canEditTable(permissions)}
          >
            <BsPencilSquare size={16} color={componentPalette.blueGrey} />
          </Button>
          <Button
            className="border-transparent bg-transparent"
            onClick={() => showDeleteConfirm(value)}
            disabled={!canEditTable(permissions)}
          >
            <BsTrashFill size={16} color={componentPalette.blueGrey} />
          </Button>
        </Space>
      ),
      align: "center",
    },
  ];

  return (
    <div>
      {contextHolder}

      <TankTypeModal
        form={form}
        open={showModal}
        mode={mode}
        loading={loading}
        isDuplicate={isDuplicate}
        onDuplicateChange={(isDup) => {
          setIsDuplicate(isDup);
        }}
        onCloseModal={onCloseModal}
        onSave={(mode, data) => onSave(mode, data)}
      />

      {canEditTable(permissions) && (
        <div style={{ display: "flex", justifyContent: "end", paddingBottom: "10px" }}>
          <Button
            type="primary"
            size="middle"
            onClick={() => onOpenModal("insert", initTankType)}
            style={{
              display: "flex",
              alignItems: "center",
            }}
            disabled={!canEditTable(permissions)}
          >
            <PlusCircleOutlined />
            New
          </Button>
        </div>
      )}

      <Table
        className="w-full"
        columns={columns}
        dataSource={tankType}
        loading={loading}
        onChange={onTableChange}
        scroll={{ x: "max-content" }}
        pagination={{
          showSizeChanger: false,
          size: "small",
          current,
          pageSize,
          total: totalTankType,
        }}
      />
    </div>
  );
};

export default TankTypeTable;
